import React, { useEffect, useState } from "react";
import { Modal, ModalDialog, CircularProgress } from "@mui/joy";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch } from "../../../../../../fetch/fetch";
import { C, toastOptions, formatSigned, typeLabels, formatMonthDayTime, formatFullDateTime, thisMonth, chipStyle } from "./giftcashutil";

/**
 * 기프티콘 캐쉬 관리표 > [내역] / 헤더 [내역 전체 보기] (GET /dashboard/reviewEvent/cash/ledger)
 * branch 없으면 전체 지점 내역 (행 제목 앞에 지점명)
 * props: open, onClose, branch({key, label, balance} | null), totalBalance(전체 보기일 때 9개 지점 합계)
 */

const FILTERS = [
    { key: "", label: "전체" },
    { key: "charge", label: "충전" },
    { key: "review", label: "지도리뷰" },
    { key: "exitSurvey", label: "퇴원생설문" },
];

const GiftCashHistoryModal: React.FC<any> = (props) => {

    const [type, setType] = useState<string>("");
    const [month, setMonth] = useState<string>(thisMonth());
    const [rows, setRows] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const branchKey = props.branch ? props.branch.key : "";

    useEffect(() => {
        if (props.open) {
            setType("");
            setMonth(thisMonth());
        }
    }, [props.open, branchKey]);

    useEffect(() => {
        if (!props.open || !month) return;
        load();
    }, [props.open, branchKey, type, month]);

    const load = async () => {
        try {
            setLoading(true);
            const result: any = await getFetch(`/dashboard/reviewEvent/cash/ledger?branchKey=${branchKey}&type=${type}&month=${month}`);
            if (result.message === "success") {
                setRows(result.data || []);
                setSummary(result.summary || null);
            } else {
                console.log(result);
                toast.error("내역을 불러오지 못했습니다", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("내역을 불러오지 못했습니다", toastOptions);
        } finally {
            setLoading(false);
        }
    };

    const title = props.branch ? `${props.branch.label} 캐쉬 내역` : "전체 캐쉬 내역";
    const balance = props.branch ? +props.branch.balance || 0 : +props.totalBalance || 0;
    const monthLabel = month ? `${+month.split("-")[1]}월` : "";

    const rowTitle = (r: any) => {
        const prefix = props.branch ? "" : `[${r.branchLabel}] `;
        if (r.type === "charge") {
            return `${prefix}수동 충전 · ${r.staffName || ""}`;
        }
        return `${prefix}${typeLabels[r.type] || r.type} · ${r.receiverName || ""} (${r.receiverPhone || ""})`;
    };

    const rowSub = (r: any) => {
        const parts = [formatMonthDayTime(r.createdAt)];
        if (r.type === "charge") {
            if (r.memo) parts.push(r.memo);
        } else {
            if (r.giftName) parts.push(r.giftName);
            //본사직영 차감은 실제 지점(강남2호점·대치3층 등) 표시
            if (r.branchKey === "hq" && r.sourceLocation) parts.push(r.sourceLocation);
            if (r.type === "review" && r.staffName) parts.push(`${r.staffName} 발송`);
        }
        return parts.join(" · ");
    };

    //엑셀 내보내기 — UTF-8 BOM CSV (엑셀에서 한글 깨짐 방지)
    const exportCsv = () => {
        const header = ["일시", "지점", "실제 지점", "종류", "금액", "거래 후 잔액", "받은 사람", "전화번호", "선물 종류", "메모", "처리 직원"];
        const escape = (v: any) => {
            const s = v === null || v === undefined ? "" : String(v);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const lines = [header.join(",")].concat(rows.map((r: any) => [
            formatFullDateTime(r.createdAt), r.branchLabel, r.sourceLocation, typeLabels[r.type] || r.type,
            r.amount, r.balanceAfter, r.receiverName, r.receiverPhone, r.giftName, r.memo, r.staffName,
        ].map(escape).join(",")));
        const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `기프티콘캐쉬_${props.branch ? props.branch.label : "전체"}_${month}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <Modal open={props.open} onClose={props.onClose}>
            <ModalDialog sx={{ width: props.branch ? 420 : 480, maxWidth: "calc(100vw - 32px)", height: 640, maxHeight: "calc(100vh - 32px)", p: 0, borderRadius: "12px", border: `1px solid ${C.border}`, overflow: "hidden", gap: 0, color: C.text, display: "flex", flexDirection: "column" }}>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: `1px solid ${C.line}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <button type="button" aria-label="뒤로" onClick={props.onClose} style={{ width: 32, height: 32, border: 0, background: "transparent", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.gray} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6"></path></svg>
                        </button>
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <div style={{ fontSize: 16, fontWeight: 700 }}>{title}</div>
                            <div style={{ fontSize: 12, color: C.gray }}>충전(+) · 지도리뷰 / 퇴원생설문 기프티콘 차감(−)</div>
                        </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 11, color: C.gray }}>{props.branch ? "현재 잔액" : "지점 합계"}</div>
                        <div style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: balance < 0 ? C.red : C.text }}>{formatSigned(balance)}원</div>
                    </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "12px 20px", borderBottom: `1px solid ${C.line}` }}>
                    {FILTERS.map((f) => (
                        <button key={f.key} type="button" onClick={() => setType(f.key)} style={chipStyle(type === f.key)}>{f.label}</button>
                    ))}
                    <input
                        type="month"
                        aria-label="월 선택"
                        value={month}
                        max={thisMonth()}
                        onChange={(e) => setMonth(e.target.value)}
                        style={{ ...chipStyle(false), marginLeft: "auto", padding: "0 10px", outline: 0 }}
                    />
                </div>

                <div style={{ display: "flex", flexDirection: "column", flex: 1, overflowY: "auto" }}>
                    {loading &&
                        <div style={{ display: "flex", justifyContent: "center", padding: 32 }}><CircularProgress size="sm" /></div>
                    }
                    {!loading && rows.length === 0 &&
                        <div style={{ padding: 32, textAlign: "center", fontSize: 13, color: C.gray }}>{monthLabel} 내역이 없어요</div>
                    }
                    {!loading && rows.map((r: any) => {
                        const isCharge = r.type === "charge";
                        return (
                            <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 20px", borderBottom: `1px solid ${C.rowLine}`, background: isCharge ? C.blueBg : "#FFFFFF" }}>
                                <div style={{ width: 32, height: 32, borderRadius: 8, background: isCharge ? C.blueIconBg : C.redBg, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
                                    {isCharge ?
                                        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14"></path></svg>
                                        :
                                        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.red} strokeWidth="2" strokeLinecap="round"><path d="M5 12h14"></path></svg>
                                    }
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{rowTitle(r)}</div>
                                    <div style={{ fontSize: 11, color: C.gray, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{rowSub(r)}</div>
                                </div>
                                <div style={{ textAlign: "right", flex: "none" }}>
                                    <div style={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: isCharge ? C.blue : C.red }}>{+r.amount > 0 ? "+" : ""}{formatSigned(+r.amount)}</div>
                                    <div style={{ fontSize: 11, color: +r.balanceAfter < 0 ? C.red : C.gray, fontVariantNumeric: "tabular-nums" }}>{formatSigned(+r.balanceAfter)}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderTop: `1px solid ${C.line}`, background: C.grayBg, fontSize: 12, color: C.gray }}>
                    <span>
                        {summary ?
                            `${monthLabel} 충전 ${summary.charge.count} · 지도리뷰 ${summary.review.count} · 퇴원생설문 ${summary.exitSurvey.count}`
                            : `${monthLabel}`}
                    </span>
                    <a href="#" onClick={(e) => { e.preventDefault(); if (rows.length) exportCsv(); }} style={{ fontSize: 12, fontWeight: 500, textDecoration: "none", color: rows.length ? C.blue : C.gray, cursor: rows.length ? "pointer" : "default" }}>
                        엑셀 내보내기
                    </a>
                </div>
            </ModalDialog>
        </Modal>
    );
};

export default GiftCashHistoryModal;
